import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AlertBanners from "./components/AlertBanners";
import HomePage from "./pages/HomePage";
import Team from "./pages/Team";
import ReportPiracyPage from "./pages/ReportPiracyPage";
import Vacancies from "./pages/Vacancies";
import News from "./pages/News";
import Partners from "./pages/Partners";
import Community from "./pages/Community";
import LoungeComingSoon from "./components/LoungeComingSoon";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import AccountSettings from "./pages/AccountSettings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import StaffLogin from "./pages/StaffLogin";
import StaffDashboardNew from "./pages/StaffDashboardNew";
import ConferenceGuestPage from "./pages/ConferenceGuestPage";
import StatusPage from "./pages/StatusPage";
import MaintenancePage from "./pages/MaintenancePage";
import ChatWidget from "./components/ChatWidget";
import PolicyAcceptanceModal from "./components/PolicyAcceptanceModal";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AppContent() {
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(null);
  
  // Don't show chat widget on staff pages
  const showChatWidget = !location.pathname.startsWith('/staff/');
  const isStaffRoute = location.pathname.startsWith('/staff/');
  const isStatusRoute = location.pathname === '/system-status';
  const isAccountRoute = location.pathname.startsWith('/account/');
  // Emergency-unlock routes: staff/login and the system-status page must NEVER be gated by
  // the maintenance screen, otherwise an executive who accidentally enables full lockdown
  // has no way back in to switch it off.
  const isStaffLoginRoute = location.pathname === '/staff/login';

  // If a superadmin / executive is signed in, they always bypass maintenance so they can
  // reach the toggle. Everyone else still sees the maintenance page.
  const cachedUser = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || 'null'); } catch { return null; }
  })();
  const isPrivilegedAdmin = cachedUser && ['superadmin', 'executive'].includes(cachedUser.role);

  useEffect(() => {
    const portal = isStaffRoute ? 'staff' : 'public';
    axios.get(`${BACKEND_URL}/api/maintenance/check?portal=${portal}`).then(r => setMaintenance(r.data)).catch(() => {});
  }, [location.pathname, isStaffRoute]);

  // Full lockdown — for the active portal scope
  if (
    maintenance?.enabled &&
    !maintenance?.allow_browse &&
    !isStatusRoute &&
    !isStaffLoginRoute &&
    !isPrivilegedAdmin
  ) {
    return <MaintenancePage title={maintenance.title} message={maintenance.message} scheduledEnd={maintenance.scheduled_end} />;
  }

  return (
    <>
      {/* Alert banners on public pages (not staff, status, or account hub) */}
      {!isStaffRoute && !isStatusRoute && !isAccountRoute && <AlertBanners />}
      <Routes>
        {/* Public routes with header/footer */}
        <Route path="/" element={<><Header /><HomePage /><Footer /></>} />
        <Route path="/team" element={<><Header /><Team /><Footer /></>} />
        <Route path="/report-piracy" element={<><Header /><ReportPiracyPage /><Footer /></>} />
        <Route path="/vacancies" element={<><Header /><Vacancies /><Footer /></>} />
        <Route path="/news" element={<><Header /><News /><Footer /></>} />
        <Route path="/partners" element={<><Header /><Partners /><Footer /></>} />
        <Route path="/privacy" element={<><Header /><PrivacyPolicy /><Footer /></>} />
        <Route path="/system-status" element={<StatusPage />} />
        
        {/* Auth routes without header/footer */}
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        
        {/* Account routes with header/footer */}
        <Route path="/account/settings" element={<><AccountSettings /><Footer /></>} />
        <Route path="/account/lounge" element={<div className="min-h-screen bg-[#05050A] px-4 py-10"><div className="max-w-5xl mx-auto"><LoungeComingSoon /></div></div>} />
        
        {/* Staff routes without header/footer */}
        <Route path="/staff/login" element={<StaffLogin />} />
        <Route path="/staff/*" element={<StaffDashboardNew />} />
        
        {/* Conference guest route - public access */}
        <Route path="/conference/:roomId" element={<ConferenceGuestPage />} />
      </Routes>
      <Toaster position="top-right" richColors />
      {showChatWidget && <ChatWidget />}
      {!isStaffRoute && <PolicyAcceptanceModal />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
